<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  responses: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const truncate = (text, length = 100) => {
  if (!text) return '-';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const handleApprove = response => {
  emit('action', { action: 'approve', id: response.id });
};

const handleEdit = response => {
  emit('action', { action: 'edit', id: response.id });
};

const handleDelete = response => {
  emit('action', { action: 'delete', id: response.id });
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
  >
    <table class="w-full">
      <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
        <tr>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-message-circle size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.RESPONSES.TABLE.QUESTION') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-text size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.RESPONSES.TABLE.ANSWER') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-activity size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.RESPONSES.TABLE.STATUS') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-calendar size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.RESPONSES.TABLE.CREATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11"
          >
            <div class="flex items-center justify-end gap-1.5">
              <span>{{ t('CAPTAIN.RESPONSES.TABLE.ACTIONS') }}</span>
              <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-n-solid-1">
        <tr
          v-for="response in responses"
          :key="response.id"
          class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
        >
          <td class="px-5 py-3">
            <span class="text-sm font-medium text-n-slate-12 block">
              {{ response.question }}
            </span>
          </td>
          <td class="px-5 py-3">
            <span class="text-sm text-n-slate-10 line-clamp-2">
              {{ truncate(response.answer, 100) }}
            </span>
          </td>
          <td class="px-5 py-3">
            <div
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
              :class="[
                response.status === 'approved'
                  ? 'bg-n-teal-2 text-n-teal-11 border-n-teal-6'
                  : 'bg-n-orange-2 text-n-orange-11 border-n-orange-6',
              ]"
            >
              <div
                class="w-1.5 h-1.5 rounded-full"
                :class="[
                  response.status === 'approved'
                    ? 'bg-n-teal-9 animate-pulse'
                    : 'bg-n-orange-9',
                ]"
              />
              {{
                response.status === 'approved'
                  ? t('CAPTAIN.RESPONSES.STATUS.APPROVED')
                  : t('CAPTAIN.RESPONSES.STATUS.PENDING')
              }}
            </div>
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatDate(response.createdAt || response.created_at) }}
          </td>
          <td class="px-5 py-3">
            <div class="flex justify-end gap-2">
              <Button
                v-if="response.status === 'pending'"
                icon="i-lucide-check"
                variant="ghost"
                size="sm"
                color="teal"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.RESPONSES.TABLE.APPROVE')"
                @click="handleApprove(response)"
              />
              <Button
                icon="i-lucide-edit"
                variant="ghost"
                size="sm"
                color="blue"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.RESPONSES.TABLE.EDIT')"
                @click="handleEdit(response)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.RESPONSES.TABLE.DELETE')"
                @click="handleDelete(response)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
